import { Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useRetryXeroItems, useXeroSyncIssues, type XeroSyncIssue } from '@/hooks/useXeroConnection';

const ENTITY_LABELS: Record<string, string> = {
  contact: 'Customer',
  invoice: 'Invoice',
  payment: 'Payment',
  credit_note: 'Refund',
};

const STATE_LABELS: Record<string, string> = {
  failed: 'Failed',
  dead: 'Gave up',
  skipped: 'Skipped',
  drift: 'Differs in Xero',
  locked: 'Locked in Xero',
  error: 'Error',
  orphaned: 'Missing in Xero',
};

const SKIP_REASONS: Record<string, string> = {
  needs_credit_note: 'Cancelled here but already paid in Xero. Record a refund to raise a credit note, or void it in Xero.',
  locked_remote_payments: 'Xero has payments on this invoice, so its content can no longer be changed from here.',
  invoice_not_synced: 'The invoice has not reached Xero yet.',
};

function issueTone(state: string): string {
  if (state === 'failed' || state === 'dead' || state === 'error') return 'border-destructive/40 bg-destructive/5 text-destructive';
  if (state === 'drift' || state === 'locked' || state === 'skipped') return 'border-warning/40 bg-warning/10 text-warning';
  return 'border-border bg-muted text-muted-foreground';
}

function canRetry(issue: XeroSyncIssue): boolean {
  return issue.kind === 'queue' && (issue.state === 'failed' || issue.state === 'dead' || issue.error_kind === 'invoice_not_synced');
}

/** Failed / stuck pushes and links that need a human, with a one-click retry. */
export function XeroSyncIssuesList({ enabled }: { enabled: boolean }) {
  const issues = useXeroSyncIssues(enabled);
  const retry = useRetryXeroItems();
  const { formatShortDate } = useCompanyLocale();

  if (!enabled) return null;
  if (issues.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking for sync issues…
      </div>
    );
  }
  const list = issues.data ?? [];
  if (list.length === 0) {
    return <p className="text-sm text-muted-foreground">No sync issues. Everything that should be in Xero is there.</p>;
  }

  const handleRetry = async (ids: string[]) => {
    try {
      const n = await retry.mutateAsync(ids);
      toast({ title: n === 1 ? 'Queued for another try' : `${n} items queued for another try` });
    } catch (e) {
      toast({ title: 'Could not retry', description: e instanceof Error ? e.message : 'Unknown error', variant: 'destructive' });
    }
  };

  const retryable = list.filter(canRetry).map((i) => i.id);

  return (
    <div className="space-y-2">
      {retryable.length > 1 && (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => void handleRetry(retryable)} disabled={retry.isPending}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Retry all ({retryable.length})
          </Button>
        </div>
      )}
      <ul className="space-y-2">
        {list.map((issue) => (
          <li key={`${issue.kind}-${issue.id}`} className="rounded-lg border p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">
                {ENTITY_LABELS[issue.entity_type] ?? issue.entity_type} {issue.label ? `· ${issue.label}` : ''}
              </span>
              <Badge variant="outline" className={`text-[11px] ${issueTone(issue.state)}`}>
                {STATE_LABELS[issue.state] ?? issue.state}
              </Badge>
              <span className="ml-auto text-xs text-muted-foreground">{formatShortDate(issue.occurred_at)}</span>
            </div>
            <div className="mt-1 text-muted-foreground">
              {(issue.error_kind && SKIP_REASONS[issue.error_kind]) || issue.detail || 'No details recorded.'}
            </div>
            {canRetry(issue) && (
              <div className="mt-2">
                <Button size="sm" variant="ghost" onClick={() => void handleRetry([issue.id])} disabled={retry.isPending}>
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                  Retry
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
